import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { subscriptionsAPI, paymentsAPI, listingsAPI, categoriesAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Check, Store, X, RotateCcw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import AddonPurchaseModal from '../components/AddonPurchaseModal';
import BusinessNameModal from '../components/BusinessNameModal';
import { isIosNativeApp, isAndroidNativeApp, loadAppleProductMap, purchaseApple, restoreApplePurchases } from '../utils/applePay';

const ADDON_DEFS = [
  { addon_id: 'pro_listing', name: 'Pro Listing', price: 25, blurb: 'Up to 25 images per listing', kind: 'pro', color: '#1800ad',
    info: 'Pro Listing badge with extended gallery (up to 25 images). Highlights your ad in search results and lifts buyer trust. Duration: lifetime of the listing. Impact: ~3× more inquiries.' },
  { addon_id: 'boost_entry', name: 'Boost (Entry)', price: 9, blurb: '3-day boost for one listing', kind: 'boost', color: '#ff8500',
    info: 'Pushes your listing to the top of category & search results for 3 days. Adds a "BOOSTED" badge for buyer attention. Impact: 5-8× more views during the boost window.' },
  { addon_id: 'boost_standard', name: 'Boost (Standard)', price: 19, blurb: '7-day boost for one listing', kind: 'boost', color: '#ff8500',
    info: 'Pushes your listing to the top for 7 full days with a "BOOSTED" badge. Best value for high-ticket items. Impact: 8-12× more views vs an unboosted listing.' },
];

// On iOS, the ₹9 Boost (Entry) add-on is below Apple's minimum price tier
// (~₹19) — Apple won't approve it, so we hide it entirely on iOS native.
const visibleAddons = isIosNativeApp
  ? ADDON_DEFS.filter((a) => a.addon_id !== 'boost_entry')
  : ADDON_DEFS;

// Server-side reconciliation poll — after returning from the UPI app the
// Razorpay `handler` callback is unreliable inside the Android WebView. Polling
// the backend (which confirms with Razorpay and activates the plan idempotently)
// guarantees the subscription is applied even if `handler` never fires.
const pollPaymentFulfillment = async (orderId, shouldStop, onFulfilled) => {
  const startedAt = Date.now();
  const MAX_MS = 3 * 60 * 1000;
  while (Date.now() - startedAt < MAX_MS) {
    await new Promise((r) => setTimeout(r, 4000));
    if (shouldStop()) return;
    try {
      const { data } = await paymentsAPI.status(orderId);
      if (data?.fulfilled) { onFulfilled(data); return; }
    } catch (_) { /* transient — keep polling */ }
  }
};

const Subscriptions = () => {
  const [plans, setPlans] = useState([]);
  const [dealerGroups, setDealerGroups] = useState([]);
  // Issue #5: scope "Processing..." to only the plan being purchased.
  // Holds the plan_type string of the in-flight purchase, or '' when idle.
  const [processingPlan, setProcessingPlan] = useState('');
  const [activeAddon, setActiveAddon] = useState(null);
  const [businessNameModal, setBusinessNameModal] = useState({ open: false, categoryLabel: '', collectAlternatePhone: false });
  // Issue #4: tap-toggleable tooltip for the addon info "i" button (mobile-friendly).
  // Holds the addon_id whose tooltip is open, or '' when none.
  const [openAddonInfo, setOpenAddonInfo] = useState('');
  const addonInfoRef = useRef(null);
  // iOS-only — Apple Product ID lookup, populated once. When empty on iOS we
  // disable purchase buttons because we can't map plan→product.
  const [appleProductMap, setAppleProductMap] = useState({});
  const [restoring, setRestoring] = useState(false);
  // iter125 — When arriving here via `?restore_listing=<id>`, resolve the ad's
  // category so we can highlight only the plans that can restore it. Dealer
  // plans in other categories are dimmed with a clear "not eligible" hint,
  // matching the 402 rules enforced by POST /listings/{id}/reactivate.
  const [restoreListing, setRestoreListing] = useState(null); // {title, category_id}
  const [restoreCategoryLabel, setRestoreCategoryLabel] = useState('');
  const eligibleAnchorRef = useRef(null);
  // iter141 (Phase C) — Synchronous double-tap lock. React state updates are
  // async so `processingPlan`/`disabled` won't propagate fast enough to block
  // a rapid double-tap on Android WebViews. Ref flips synchronously.
  const purchasingRef = useRef(false);
  // Guards against the handler AND the reconciliation poll both finishing.
  const finishedRef = useRef(false);
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return');
  const restoreListingId = searchParams.get('restore_listing');

  // Helper: where to land after a successful purchase.
  // 1. restore_listing → reactivate the archived listing (backend auto-restored
  //    images via _auto_restore_archived_listings hook in subscription.purchase)
  //    and deep-link to its detail page.
  // 2. return=postad → resume the post-ad draft.
  // 3. default → dashboard.
  const finishPurchase = async (toastMsg) => {
    toast.success(toastMsg);
    if (restoreListingId) {
      // Iter43 Fix #6: if `return=edit-listing`, send the user back to the edit
      // page so they can publish/reactivate WITHOUT losing their session draft.
      if (returnTo === 'edit-listing') {
        const action = searchParams.get('action') || '';
        navigate(`/edit-listing/${restoreListingId}?resume=1${action ? `&action=${encodeURIComponent(action)}` : ''}`);
        return;
      }
      try {
        await listingsAPI.reactivate(restoreListingId);
        toast.success('Your ad has been restored.');
      } catch (e) {
        // Auto-restore hook should have already brought it back; ignore secondary call failures
      }
      navigate(`/listing/${restoreListingId}`);
      return;
    }
    navigate(returnTo === 'postad' ? '/post-ad?resume=1' : '/dashboard');
  };

  useEffect(() => {
    // Issue #3: fetch regular and dealer plans INDEPENDENTLY so neither blocks
    // the other on slow networks. Each section renders as soon as its data lands.
    subscriptionsAPI.getPlans()
      .then((r) => setPlans(r.data || []))
      .catch((e) => { console.error('Failed to load regular plans:', e); toast.error('Failed to load plans'); });
    subscriptionsAPI.getDealerPlans()
      .then((r) => setDealerGroups(r.data?.grouped || []))
      .catch((e) => { console.error('Failed to load dealer plans:', e); });
    if (isIosNativeApp) {
      loadAppleProductMap().then(setAppleProductMap);
    }
    window.scrollTo(0, 0);
  }, []);

  // iter125 — Resolve the target listing (title + category) when arriving via
  // ?restore_listing=<id>. Owner-authenticated call: iter123 backend guard
  // returns 200 to the owner even if the listing is expired/sold/hidden.
  useEffect(() => {
    if (!restoreListingId) return;
    let cancelled = false;
    (async () => {
      try {
        const listingResp = await listingsAPI.getById(restoreListingId);
        if (cancelled) return;
        const lst = listingResp.data || {};
        setRestoreListing({ title: lst.title, category_id: lst.category_id });
        try {
          const catsResp = await categoriesAPI.getAll();
          if (cancelled) return;
          const match = (catsResp.data || []).find((c) => c.category_id === lst.category_id);
          if (match) setRestoreCategoryLabel(match.name);
        } catch (_) { /* fallback: leave label empty */ }
      } catch (_) {
        // If we can't resolve the listing, silently skip the banner —
        // the user still sees the full plan grid and normal purchase flow.
      }
    })();
    return () => { cancelled = true; };
  }, [restoreListingId]);

  // iter125 — Eligibility helper. A plan can restore a given category iff it's
  // either a non-dealer plan (covers all categories) OR a dealer plan whose
  // dealer_category_id matches the listing's category. Mirrors the server-side
  // filter at server.py reactivate_listing() so client + server never diverge.
  const isPlanEligibleForRestore = (plan) => {
    if (!restoreListing) return true;
    if (!plan.is_dealer_plan) return true;
    return plan.dealer_category_id === restoreListing.category_id;
  };

  // Scroll to first eligible section once both the listing & plans are known.
  useEffect(() => {
    if (!restoreListing) return;
    if (!eligibleAnchorRef.current) return;
    const t = setTimeout(() => {
      try { eligibleAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [restoreListing, plans.length, dealerGroups.length]);

  // iter136 — Deep-link support for /subscriptions#dealer-plans. When the
  // homepage hero "Dealer Plans" button lands here, wait until dealerGroups
  // is populated (data fetch is async) then smooth-scroll to the section.
  useEffect(() => {
    if (dealerGroups.length === 0) return;
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#dealer-plans') return;
    const t = setTimeout(() => {
      const el = document.getElementById('dealer-plans');
      if (el) {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) { /* ignore */ }
      }
    }, 150);
    return () => clearTimeout(t);
  }, [dealerGroups.length]);

  // Dismiss the tap-tooltip on outside click / scroll
  useEffect(() => {
    if (!openAddonInfo) return;
    const onDocClick = (e) => {
      if (addonInfoRef.current && !addonInfoRef.current.contains(e.target)) {
        setOpenAddonInfo('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [openAddonInfo]);

  const handlePurchase = async (plan) => {
    if (!user) {
      navigate('/login');
      return;
    }
    // iter141 (Phase C) — Double-tap idempotency guard. React `setProcessingPlan`
    // is async; a rapid double-tap in Android WebViews (touchstart+click firing
    // twice) can enter this handler TWICE before the disabled prop propagates,
    // which historically created two Razorpay orders + two subscription docs
    // for a single payment. A useRef lock closes the window synchronously.
    if (purchasingRef.current) return;
    purchasingRef.current = true;

    // iOS native app → use Apple In-App Purchase, never Razorpay (App Store
    // Guideline 3.1.1). The receipt is verified server-side which both grants
    // the subscription and triggers any dealer/business-name follow-up.
    if (isIosNativeApp) {
      setProcessingPlan(plan.plan_type);
      // Immediate user feedback — Apple reviewers reject when a tap looks dead.
      // Resolved when the StoreKit sheet appears OR an error toast fires.
      toast.info('Opening App Store…');
      try {
        // Make sure the catalog is loaded BEFORE the user taps Buy. If the
        // initial fetch hasn't landed yet (slow network), wait for it here
        // rather than silently failing.
        let map = appleProductMap;
        if (!map || Object.keys(map).length === 0) {
          map = await loadAppleProductMap();
          setAppleProductMap(map);
        }
        const productId = map[plan.plan_type];
        if (!productId) {
          toast.error('This plan is not available on iOS yet. Please try again later.');
          return;
        }
        await purchaseApple({ productId });
        if (plan.is_dealer_plan) {
          toast.success('Dealer plan activated! Just one more step…');
          setBusinessNameModal({
            open: true,
            categoryLabel: plan.category_label || '',
            collectAlternatePhone: plan.dealer_category_id === 'cat_property',
          });
          try { await refresh?.(); } catch (_) { /* ignore */ }
          return;
        }
        await finishPurchase('Subscription activated successfully!');
      } catch (error) {
        console.error('Apple IAP failed:', error);
        toast.error(error?.response?.data?.detail || error?.message || 'Purchase failed. Please try again.');
      } finally {
        setProcessingPlan('');
      }
      return;
    }

    setProcessingPlan(plan.plan_type);
    try {
      finishedRef.current = false;
      const amount = plan.price * 100;
      // For dealer plans, tag purpose so admin revenue aggregation can pick it up
      const purpose = plan.is_dealer_plan
        ? `subscription_${plan.plan_type}`
        : `Subscription: ${plan.name}`;
      const orderResponse = await paymentsAPI.createOrder(amount, purpose, { plan_type: plan.plan_type });

      // Shared success routine — dealer plans branch to the Business Name modal.
      const activated = async (fulfillment) => {
        if (finishedRef.current) return;
        finishedRef.current = true;
        const f = fulfillment || {};
        if (f.is_dealer_plan && f.needs_business_name) {
          toast.success('Dealer plan activated! Just one more step…');
          setBusinessNameModal({
            open: true,
            categoryLabel: plan.category_label || '',
            collectAlternatePhone: plan.dealer_category_id === 'cat_property',
          });
          try { await refresh?.(); } catch (_) { /* ignore */ }
        } else {
          await finishPurchase('Subscription activated successfully!');
        }
      };

      const options = {
        key: orderResponse.data.key_id,
        amount: orderResponse.data.amount,
        currency: "INR",
        name: "GenzoX Marketplace",
        description: plan.name,
        order_id: orderResponse.data.order_id,
        handler: async function (response) {
          try {
            // Verify payment signature server-side before activating (security critical for live mode)
            await paymentsAPI.verify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            // Order-bound (idempotent) activation — the poll / webhook may also
            // fire; whichever lands first wins, no double grant.
            const purchaseResp = await subscriptionsAPI.purchase(plan.plan_type, response.razorpay_order_id);
            await activated(purchaseResp.data);
          } catch (error) {
            console.error('Subscription activation failed:', error);
            toast.error('Payment received but verification failed. Please contact support with your payment ID: ' + response.razorpay_payment_id);
          }
        },
        prefill: {
          name: user.name,
          email: user.email || '',
          contact: user.phone || ''
        },
        theme: {
          color: "#1800ad"
        },
        // iter140 — Android WebToNative shell: emit `upi://` intent links so
        // the native WebView can hand off to GPay/PhonePe/Paytm (Razorpay
        // UPI Intent flow). No effect on web or iOS (iOS uses Apple IAP).
        ...(isAndroidNativeApp ? { webview_intent: true } : {}),
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      // Reconciliation net: activates the plan via the backend even if the
      // Razorpay handler never fires (UPI Intent in the Android WebView).
      pollPaymentFulfillment(
        orderResponse.data.order_id,
        () => finishedRef.current,
        (data) => { activated(data?.fulfillment); },
      );
    } catch (error) {
      console.error('Payment initiation failed:', error);
      // No silent "test mode" grant — a plan must never be activated without a
      // completed, verified payment. Surface the error instead.
      toast.error(error?.response?.data?.detail || 'Payment could not be started. Please try again.');
    } finally {
      setProcessingPlan('');
      purchasingRef.current = false;
    }
  };

  const handleBusinessNameClose = async (saved) => {
    setBusinessNameModal({ open: false, categoryLabel: '' });
    try { await refresh?.(); } catch (_) { /* ignore */ }
    await finishPurchase(saved ? 'You are now a verified GenzoX Dealer!' : 'Subscription activated');
  };

  // iOS only — re-pull receipt from device + grant any unrecorded transactions.
  // Useful after reinstall / device change / accidental data wipe.
  const handleRestorePurchases = async () => {
    if (!user) { navigate('/login'); return; }
    setRestoring(true);
    try {
      const data = await restoreApplePurchases();
      const restored = data?.restored || 0;
      if (restored > 0) {
        toast.success(`Restored ${restored} purchase${restored > 1 ? 's' : ''}.`);
        try { await refresh?.(); } catch (_) { /* ignore */ }
        navigate('/dashboard');
      } else {
        toast.info('No new purchases to restore.');
      }
    } catch (error) {
      console.error('Apple restore failed:', error);
      toast.error(error?.response?.data?.detail || error?.message || 'Could not restore purchases');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <Link to="/" className="text-[#4B5563] hover:text-[#1800ad] font-medium mb-4 inline-block">← Back to Home</Link>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#0A0B10] mb-4" style={{fontFamily: 'Google Sans Flex'}} data-testid="subscriptions-title">
            Choose Your Plan
          </h1>
          <p className="text-xl text-[#4B5563]">Start selling today with our flexible subscription plans</p>
          {isIosNativeApp && (
            <button
              type="button"
              data-testid="restore-purchases-btn"
              onClick={handleRestorePurchases}
              disabled={restoring}
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#1800ad] hover:text-[#1200a0] disabled:opacity-50"
            >
              <RotateCcw size={14} className={restoring ? 'animate-spin' : ''} />
              {restoring ? 'Restoring…' : 'Restore Purchases'}
            </button>
          )}
        </div>

        {/* iter125 — Restore-Ad context banner. Shown only when the user was
             sent here from a 402 on POST /listings/{id}/reactivate. Explains
             which ad is being restored, its category, and how the eligible
             plans below are highlighted. */}
        {restoreListing && (
          <div
            ref={eligibleAnchorRef}
            data-testid="restore-context-banner"
            className="mb-10 rounded-2xl border border-[#ff8500]/30 bg-gradient-to-r from-[#fff4ed] to-[#f0edff] p-4 sm:p-5 shadow-sm"
          >
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white flex items-center justify-center flex-shrink-0 border border-[#ff8500]/30 shadow-sm">
                <Sparkles size={18} className="text-[#ff8500]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#ff8500] mb-1">Restoring your ad</p>
                <p className="text-base sm:text-lg font-bold text-[#0A0B10] leading-tight line-clamp-1" data-testid="restore-context-title">
                  {restoreListing.title || 'Your listing'}
                </p>
                <p className="text-xs sm:text-sm text-[#4B5563] mt-1" data-testid="restore-context-hint">
                  Category: <span className="font-semibold text-[#0A0B10]">{restoreCategoryLabel || 'this category'}</span>. Pick any highlighted plan below and we&apos;ll restore your ad automatically after payment. Dealer plans locked to other categories can&apos;t be used for this ad.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6" data-testid="plans-grid">
          {plans.map((plan) => {
            const isMostPopular = plan.is_most_popular || plan.plan_type === '30_day';
            const eligibleForRestore = isPlanEligibleForRestore(plan);
            const showEligibleRing = restoreListing && eligibleForRestore;
            return (
            <div
              key={plan.plan_type}
              data-testid={`plan-${plan.plan_type}`}
              data-eligible-restore={restoreListing ? (eligibleForRestore ? '1' : '0') : undefined}
              className={`relative bg-white rounded-3xl p-7 shadow-sm border-2 transition-all duration-300 hover:shadow-xl ${
                showEligibleRing
                  ? 'border-[#10B981] ring-2 ring-[#10B981]/25 lg:scale-[1.03] shadow-lg'
                  : isMostPopular ? 'border-[#ff8500] ring-2 ring-[#ff8500]/20 lg:scale-[1.03] shadow-lg' : 'border-gray-100 hover:scale-[1.02]'
              }`}
            >
              {showEligibleRing && (
                <div
                  data-testid={`eligible-badge-${plan.plan_type}`}
                  className="absolute -top-3 right-4 bg-[#10B981] text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider shadow-md whitespace-nowrap flex items-center gap-1"
                >
                  <Check size={11} strokeWidth={3} /> Eligible for your ad
                </div>
              )}
              {isMostPopular && !showEligibleRing && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#1800ad] to-[#ff8500] text-white text-[11px] px-4 py-1 rounded-full font-bold uppercase tracking-wider shadow-md whitespace-nowrap">
                  Most Popular
                </div>
              )}

              <h3 className="text-xl font-bold text-[#0A0B10] mb-2 mt-2" style={{fontFamily: 'Google Sans Flex'}}>
                {plan.name}
              </h3>

              <div className="mb-5">
                <span className="text-4xl font-black text-[#1800ad]" style={{fontFamily: 'Google Sans Flex'}}>₹{plan.price}</span>
                <span className="text-[#9CA3AF] ml-2 text-sm">/{plan.duration_days} {plan.duration_days === 1 ? 'day' : 'days'}</span>
              </div>

              <ul className="space-y-2.5 mb-6 min-h-[160px]" data-testid={`plan-benefits-${plan.plan_type}`}>
                {(plan.benefits || []).map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-[#4B5563] leading-snug">
                    <Check size={16} className="text-[#10B981] flex-shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`purchase-${plan.plan_type}`}
                onClick={() => handlePurchase(plan)}
                disabled={processingPlan === plan.plan_type}
                className={`w-full rounded-full px-6 py-3 font-bold text-sm transition-colors shadow-lg disabled:opacity-50 ${
                  isMostPopular
                    ? 'bg-gradient-to-r from-[#1800ad] to-[#ff8500] text-white hover:opacity-90'
                    : 'bg-[#1800ad] text-white hover:bg-[#1200a0]'
                }`}
              >
                {processingPlan === plan.plan_type ? 'Processing...' : (isIosNativeApp ? 'Buy' : 'Get Started')}
              </button>
            </div>
            );
          })}
        </div>

        {/* Dealer Plans Section */}
        {dealerGroups.length > 0 && (
          <div id="dealer-plans" className="mt-20 scroll-mt-24" data-testid="dealer-plans-section">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Store size={28} className="text-[#00bf63]" />
              <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0B10]" style={{fontFamily: 'Google Sans Flex'}}>
                Dealer Plans
              </h2>
            </div>
            <p className="text-center text-[#4B5563] mb-2">High-volume plans for professional dealers</p>
            <p className="text-center text-xs text-[#4B5563] mb-10 max-w-2xl mx-auto">
              <span className="inline-block bg-[#00bf63]/10 text-[#00bf63] px-3 py-1 rounded-full font-bold mr-2">TRUSTED DEALER</span>
              badge appears on every listing. Plans are <span className="font-bold">category-restricted</span> — Mobile, Bikes, Cars and Property dealer plans each allow listings only within their own category. Property Dealer plans also include bundled Pro Listings, Boost Entries and priority placement.
            </p>

            {dealerGroups.map((group) => (
              <div key={group.category_id} className="mb-10" data-testid={`dealer-group-${group.category_id}`}>
                <h3 className="text-xl font-bold text-[#0A0B10] mb-4 flex items-center gap-2">
                  <span className="inline-block w-1 h-6 bg-[#00bf63] rounded-full"></span>
                  {group.category_label} Dealer
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {group.plans.map((plan) => {
                    const eligibleForRestore = isPlanEligibleForRestore(plan);
                    const showEligibleRing = restoreListing && eligibleForRestore;
                    const dimIneligible = restoreListing && !eligibleForRestore;
                    return (
                    <div
                      key={plan.plan_type}
                      data-testid={`plan-${plan.plan_type}`}
                      data-eligible-restore={restoreListing ? (eligibleForRestore ? '1' : '0') : undefined}
                      className={`relative bg-white rounded-3xl p-7 shadow-sm border-l-4 border-[#00bf63] border-t border-r border-b transition-all duration-300 ${
                        showEligibleRing
                          ? 'border-t-[#10B981] border-r-[#10B981] border-b-[#10B981] ring-2 ring-[#10B981]/25 shadow-lg hover:shadow-xl hover:scale-[1.02]'
                          : dimIneligible
                            ? 'border-gray-100 opacity-55 grayscale'
                            : 'border-gray-100 hover:shadow-xl hover:scale-[1.02]'
                      }`}
                    >
                      {showEligibleRing && (
                        <div
                          data-testid={`eligible-badge-${plan.plan_type}`}
                          className="absolute -top-3 right-4 bg-[#10B981] text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider shadow-md whitespace-nowrap flex items-center gap-1"
                        >
                          <Check size={11} strokeWidth={3} /> Eligible for your ad
                        </div>
                      )}
                      {dimIneligible && (
                        <div
                          data-testid={`ineligible-note-${plan.plan_type}`}
                          className="absolute -top-3 right-4 bg-[#6B7280] text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider shadow-sm whitespace-nowrap"
                        >
                          Not eligible for this ad
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xl font-bold text-[#0A0B10]" style={{fontFamily: 'Google Sans Flex'}}>
                          {plan.name}
                        </h4>
                        <span className="bg-[#00bf63] text-white text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-wider">
                          Dealer
                        </span>
                      </div>
                      <div className="mb-5">
                        <span className="text-4xl font-black text-[#00bf63]" style={{fontFamily: 'Google Sans Flex'}}>₹{plan.price}</span>
                        <span className="text-[#9CA3AF] ml-2 text-sm">/{plan.duration_days} days</span>
                      </div>
                      <ul className="space-y-2 mb-6 text-sm">
                        <li className="flex items-center gap-2 text-[#4B5563]">
                          <Check size={18} className="text-[#00bf63] flex-shrink-0" />
                          <span className="font-bold text-[#ff8500]">{plan.ads} Active Listings</span>
                        </li>
                        <li className="flex items-center gap-2 text-[#4B5563]">
                          <Check size={18} className="text-[#00bf63] flex-shrink-0" />
                          <span>{plan.duration_days} Days Validity</span>
                        </li>
                        {plan.pros > 0 && (
                          <li className="flex items-center gap-2 text-[#4B5563]" data-testid={`plan-${plan.plan_type}-pros`}>
                            <Check size={18} className="text-[#00bf63] flex-shrink-0" />
                            <span><span className="font-semibold">{plan.pros} Pro Listings</span> Included</span>
                          </li>
                        )}
                        {plan.boosts > 0 && (
                          <li className="flex items-center gap-2 text-[#4B5563]" data-testid={`plan-${plan.plan_type}-boosts`}>
                            <Check size={18} className="text-[#00bf63] flex-shrink-0" />
                            <span><span className="font-semibold">{plan.boosts} Boost Entries</span> Included</span>
                          </li>
                        )}
                        <li className="flex items-center gap-2 text-[#4B5563]">
                          <Check size={18} className="text-[#00bf63] flex-shrink-0" />
                          <span>Category: <span className="font-semibold">{plan.category_label} only</span></span>
                        </li>
                        {/* iter121 — every dealer plan shows only the Trusted Dealer badge
                             per the new hierarchy. Trusted Seller has been removed from
                             all dealer tiers (Property Dealer included). */}
                        <li className="flex items-center gap-2 text-[#4B5563]" data-testid={`plan-${plan.plan_type}-trusted-dealer`}>
                          <Check size={18} className="text-[#00bf63] flex-shrink-0" />
                          <span>Trusted Dealer Badge</span>
                        </li>
                        {plan.priority_placement && (
                          <li className="flex items-center gap-2 text-[#4B5563]" data-testid={`plan-${plan.plan_type}-priority`}>
                            <Check size={18} className="text-[#00bf63] flex-shrink-0" />
                            <span>Priority Placement</span>
                          </li>
                        )}
                        <li className="flex items-center gap-2 text-[#4B5563]">
                          <Check size={18} className="text-[#00bf63] flex-shrink-0" />
                          <span>Business Name on listings</span>
                        </li>
                      </ul>
                      {Array.isArray(plan.eligibility) && plan.eligibility.length > 0 && (
                        <div className="mb-5 -mt-2" data-testid={`plan-${plan.plan_type}-eligibility`}>
                          <p className="text-[11px] font-bold uppercase tracking-wider text-[#9CA3AF] mb-1">Eligible for</p>
                          <p className="text-xs text-[#4B5563] leading-relaxed">{plan.eligibility.join(' · ')}</p>
                        </div>
                      )}
                      <button
                        data-testid={`purchase-${plan.plan_type}`}
                        onClick={() => handlePurchase(plan)}
                        disabled={processingPlan === plan.plan_type}
                        className="w-full rounded-full px-6 py-3 font-bold transition-colors bg-[#00bf63] text-white hover:bg-[#00a554] disabled:opacity-50 shadow-md"
                      >
                        {processingPlan === plan.plan_type ? 'Processing...' : (isIosNativeApp ? 'Buy' : 'Become a Dealer')}
                      </button>
                    </div>
                  );})}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add-ons Section */}
        <div className="mt-16" data-testid="addons-section">
          <h2 className="text-3xl font-bold text-[#0A0B10] text-center mb-8" style={{fontFamily: 'Google Sans Flex'}}>Additional Add-ons</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visibleAddons.map((addon) => (
              <div key={addon.addon_id} className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100" data-testid={`addon-card-${addon.addon_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-xl font-bold text-[#0A0B10]">{addon.name}</h3>
                  {/* Tap/click toggleable tooltip — works on mobile (where :hover doesn't exist) and desktop */}
                  <div className="relative" ref={openAddonInfo === addon.addon_id ? addonInfoRef : null}>
                    <button
                      type="button"
                      data-testid={`addon-info-${addon.addon_id}`}
                      aria-label={`About ${addon.name}`}
                      aria-expanded={openAddonInfo === addon.addon_id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenAddonInfo(openAddonInfo === addon.addon_id ? '' : addon.addon_id);
                      }}
                      className="flex-shrink-0 w-7 h-7 rounded-full bg-[#f0edff] text-[#1800ad] text-xs font-bold flex items-center justify-center cursor-pointer hover:bg-[#1800ad] hover:text-white transition-colors active:scale-95"
                    >i</button>
                    {openAddonInfo === addon.addon_id && (
                      <div
                        role="tooltip"
                        data-testid={`addon-tooltip-${addon.addon_id}`}
                        className="absolute right-0 top-9 w-64 max-w-[80vw] z-30 bg-[#0A0B10] text-white text-xs leading-relaxed rounded-xl p-3 shadow-2xl"
                      >
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="font-bold text-[#ff8500] text-sm">{addon.name}</p>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setOpenAddonInfo(''); }}
                            className="text-white/70 hover:text-white -mr-1 -mt-1 p-0.5"
                            aria-label="Close"
                            data-testid={`addon-tooltip-close-${addon.addon_id}`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                        <p className="whitespace-pre-line">{addon.info}</p>
                        {/* Caret pointing at the "i" button */}
                        <span className="absolute -top-1.5 right-3 w-3 h-3 bg-[#0A0B10] rotate-45"></span>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-3xl font-black mb-4" style={{fontFamily: 'Google Sans Flex', color: addon.color}}>₹{addon.price}</p>
                <p className="text-[#4B5563] mb-6">{addon.blurb}</p>
                <button
                  data-testid={`addon-buy-${addon.addon_id}`}
                  onClick={() => {
                    if (!user) { navigate('/login'); return; }
                    setActiveAddon(addon);
                  }}
                  className={`w-full text-white rounded-full px-8 py-3 font-bold transition-colors ${addon.kind === 'pro' ? 'bg-[#1800ad] hover:bg-[#1200a0]' : 'bg-[#ff8500] hover:bg-[#E67520]'}`}
                >
                  Buy Now
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Features — iter138: tick-mark icons replaced with the product-
             approved illustrations (buyer shield / wallet / headset) from
             the uploaded assets. Tile colours, text, spacing untouched. */}
        <div className="mt-16 text-center" data-testid="features-section">
          <h2 className="text-3xl font-bold text-[#0A0B10] mb-8" style={{fontFamily: 'Google Sans Flex'}}>Why Choose GenzoX?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                <img src="/icons/verified-buyer.png" alt="Verified Buyers" data-testid="icon-verified-buyers" className="w-11 h-11 object-contain" loading="lazy" />
              </div>
              <h3 className="text-xl font-bold text-[#0A0B10] mb-2">Verified Buyers</h3>
              <p className="text-[#4B5563]">Connect with genuine buyers across India</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                <img src="/icons/secure-payments.png" alt="Secure Payments" data-testid="icon-secure-payments" className="w-11 h-11 object-contain" loading="lazy" />
              </div>
              <h3 className="text-xl font-bold text-[#0A0B10] mb-2">Secure Payments</h3>
              <p className="text-[#4B5563]">Safe and secure payment processing</p>
            </div>
            <div>
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-100">
                <img src="/icons/247-support.png" alt="24/7 Support" data-testid="icon-247-support" className="w-11 h-11 object-contain" loading="lazy" />
              </div>
              <h3 className="text-xl font-bold text-[#0A0B10] mb-2">24/7 Support</h3>
              <p className="text-[#4B5563]">Our team is always here to help</p>
            </div>
          </div>
        </div>
      </div>
      <AddonPurchaseModal
        open={!!activeAddon}
        addon={activeAddon}
        onClose={() => setActiveAddon(null)}
      />
      <BusinessNameModal
        open={businessNameModal.open}
        categoryLabel={businessNameModal.categoryLabel}
        collectAlternatePhone={businessNameModal.collectAlternatePhone}
        onClose={handleBusinessNameClose}
      />
    </div>
  );
};

export default Subscriptions;